import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { signInUrlTree } from 'app/core/auth/guards/sign-in-redirect';
import { UserService } from 'app/core/user/user.service';
import { hasRole } from 'app/core/user/user-role.utils';
import { map } from 'rxjs';

/**
 * Role guard factory.
 *
 * Usage: `canActivate: [roleGuard(['admin'])]`
 *
 * Waits for the authentication check, which only resolves true with the
 * current user loaded (roles come from `GET auth/me`), and reads
 * `UserService.currentUser` right after it: nothing waits on `user$`.
 * If the session was closed in between, it counts as not authenticated.
 *
 * Allows activation if the user has AT LEAST ONE of the allowed roles,
 * applying the legacy role compatibility of `hasRole` (a token that still
 * says `super_admin` counts as `admin`). Otherwise redirects to the access
 * denied page.
 *
 * @param allowedRoles Roles that may activate the route
 */
export const roleGuard = (
    allowedRoles: string[]
): CanActivateFn | CanActivateChildFn => (route, state) => {
    const router: Router = inject(Router);
    const userService = inject(UserService);
    const authService = inject(AuthService);

    // Check the authentication status
    return authService.check().pipe(
        map((authenticated) => {
            const user = userService.currentUser;

            // If the user is not authenticated (or the session closed in between)...
            if (!authenticated || !user) {
                // Al sign-in, con el destino en redirectURL
                return signInUrlTree(router, state.url);
            }

            if (allowedRoles.some((role) => hasRole(user, role))) {
                return true;
            }

            // User lacks the required role: show the access denied page
            return router.parseUrl('/access-denied');
        })
    );
};
